
import React, { useRef, useEffect, useState } from 'react';
import { Mail, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface LoginFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  isLoading: boolean;
  onSubmit: (e: React.FormEvent) => Promise<void>;
}

const LoginForm = ({ 
  email, 
  setEmail, 
  password, 
  setPassword, 
  isLoading, 
  onSubmit 
}: LoginFormProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  
  // Reset local loading state when parent loading state changes
  useEffect(() => {
    if (submitAttempted && !isLoading) {
      // Clear local loading state after parent state updates
      setLocalLoading(false);
      setSubmitAttempted(false);
    }
  }, [isLoading, submitAttempted]);
  
  // Ensure timeout to prevent infinite loading state
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    
    if (localLoading) {
      timeoutId = setTimeout(() => {
        setLocalLoading(false);
      }, 10000); // 10 seconds timeout as fallback
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [localLoading]);
  
  const handleSafeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isLoading || localLoading) {
      return;
    }
    
    // Set local loading state immediately
    setLocalLoading(true);
    setSubmitAttempted(true);
    
    try {
      await onSubmit(e);
    } catch (error) {
      setLocalLoading(false);
      setSubmitAttempted(false);
    }
  };
  
  // Use the most restrictive loading state
  const effectiveLoadingState = isLoading || localLoading;
  
  return (
    <form ref={formRef} onSubmit={handleSafeSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
          <Input
            id="email"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10"
            required
            disabled={effectiveLoadingState}
            aria-disabled={effectiveLoadingState ? 'true' : 'false'}
            autoComplete="username"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pl-10"
            required
            disabled={effectiveLoadingState}
            aria-disabled={effectiveLoadingState ? 'true' : 'false'}
            autoComplete="current-password"
          />
        </div>
      </div>
      
      <Button 
        type="submit" 
        className="w-full bg-brand-blue hover:bg-brand-blue/90"
        disabled={effectiveLoadingState}
        aria-disabled={effectiveLoadingState ? 'true' : 'false'}
      >
        {effectiveLoadingState ? "Logging in..." : "Log In"}
      </Button>
    </form>
  );
};

export default LoginForm;
